import { urlForImage } from 'lib/sanity.image'
import Link from 'next/link'
import { ResponsiveImage } from './ResponsiveImage'

export const Hotspots = ({ spots }) => {
  if (!spots) return null
  return spots?.map((spot) => {
    switch (spot._type) {
      case 'hotspot':
        return <ProductHotspot key={spot._key} spot={spot} />
      case 'hotspotText':
        return <TextHotspot key={spot._key} spot={spot} />
      case 'hotspotLink':
        return <LinkHotspot key={spot._key} spot={spot} />
      case 'hotspotLinkWithProducts':
        return <LinkWithProductsHotspot key={spot._key} spot={spot} />
      default:
        return null
    }
  })
}

const LinkWithProductsHotspot = ({ spot }) => {
  return (
    <div
      className="group absolute bottom-20 left-40 z-[1] flex w-[calc(100%-80px)] items-center justify-between gap-10 rounded-8 bg-white p-10 text-12 leading-110 text-midGray transition duration-300 hover:scale-[1.01] hover:shadow-sm md:bottom-40 md:bottom-[unset] md:left-[var(--pos-x)] md:top-[var(--pos-y)] md:w-auto md:translate-y-[-50%]"
      style={
        {
          '--pos-x': `${spot.x}%`,
          '--pos-y': `${spot.y}%`,
        } as any
      }
    >
      <div className="flex gap-6">
        {spot.products.map((product) => {
          return (
            <Link
              scroll={false}
              href={`/products/${product.slug}`}
              key={product._id}
              className="relative aspect-[2/2.75] h-[50px] overflow-hidden rounded-6 md:h-[100px]"
            >
              <ResponsiveImage src={product.imgUrl} alt={product.title} />
            </Link>
          )
        })}
      </div>
      <Link
        scroll={false}
        href={`/${spot?.link?._type}s/${spot?.link?.slug}`}
        className="px-10 text-center md:underline"
      >
        <span className="md:block">Shop</span>
        <span className="pl-4 md:block md:pl-0">{spot.label}</span>
      </Link>
    </div>
  )
}

const LinkHotspot = ({ spot }) => {
  return (
    <div
      className="group absolute bottom-20 left-40 z-[1] flex w-[calc(100%-80px)] items-center justify-between gap-10 rounded-8 bg-white p-10 text-12 leading-110 text-midGray transition duration-300 hover:shadow-sm md:bottom-40 md:bottom-[unset] md:left-[var(--pos-x)] md:top-[var(--pos-y)] md:w-auto md:min-w-[300px] md:translate-y-[-50%] md:hover:scale-[1.01]"
      style={
        {
          '--pos-x': `${spot.x}%`,
          '--pos-y': `${spot.y}%`,
        } as any
      }
    >
      <div className="flex-1">
        <div className="inline-block rounded-full bg-lighterGray px-6 py-4 text-8 uppercase md:mb-12">
          {spot.tag}
        </div>
        <div className="hidden md:block">
          <div className="pb-6">{spot.label}</div>
          <Link
            scroll={false}
            href={`/${spot.link._type}s/${spot.link.slug}`}
            className="underline"
          >
            Shop Now
          </Link>
        </div>
      </div>
      <Link
        scroll={false}
        href={`/${spot.link._type}s/${spot.link.slug}`}
        className="md:hidden"
      >
        Shop the {spot.link.title}{' '}
        <span className="capitalize">{spot.link._type}</span>
      </Link>
      {spot.image ? (
        <div className="relative hidden aspect-[2/2.75] h-[70px] overflow-hidden rounded-6 md:block">
          <ResponsiveImage
            src={urlForImage(spot.image).auto('format').url()}
            alt={spot.image.alt}
          />
        </div>
      ) : null}
    </div>
  )
}

const TextHotspot = ({ spot }) => {
  return (
    <div
      className="group absolute z-[1] flex hidden rounded-8 bg-yellow px-16 py-14 text-11 leading-110 text-darkGray md:left-[var(--pos-x)] md:top-[var(--pos-y)] md:block"
      style={
        {
          '--pos-x': `${spot.x}%`,
          '--pos-y': `${spot.y}%`,
          transform: `translateY(-50%)`,
        } as any
      }
    >
      {spot.content}
    </div>
  )
}

const ProductHotspot = ({ spot }) => {
  return (
    <div
      className="group absolute left-[var(--pos-x)] top-[var(--pos-y)] z-[1] flex h-40 w-40 items-center justify-center"
      style={
        {
          '--pos-x': `${spot.x}%`,
          '--pos-y': `${spot.y}%`,
          transform: `translate(-50%, -50%)`,
        } as any
      }
    >
      <div className="ease relative flex h-24 w-24 items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-110 group-active:scale-110">
        <div className="absolute inset-0 h-full w-full rounded-full bg-[green] bg-white bg-opacity-50 backdrop-blur-sm group-hover:animate-spot" />
        <div className="relative z-[1] h-10 w-10 scale-[1.2] rounded-full bg-white group-hover:animate-spot-inner"></div>
      </div>
      <Link
        scroll={false}
        href={`/products/${spot.product.slug}`}
        className={`
          ease absolute aspect-[2/2.5] w-[120px] overflow-hidden rounded-8 bg-white opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-active:opacity-100
          ${spot.x > 80 ? 'right-32' : 'left-32'}
          ${spot.y > 80 ? 'bottom-32' : 'top-32'}
        `}
      >
        <ResponsiveImage src={spot.product.imgUrl} alt={spot.product.title} />
      </Link>
    </div>
  )
}
